/* sugar:assets/slider-chrome.js@2.2.0 */
/*
  Drives the shared slider chrome: the dots, the bar, the progress fill and the
  arrow buttons that snippets/slider-chrome.liquid renders around a Splide
  track.

  Pagination is built here rather than by Splide. Splide's own pagination can
  only draw plain dots, and getting one dot per slide out of it needs
  options.focus = 0, which quietly changes how the track scrolls. Building the
  buttons here keeps the dot count independent of scroll behaviour and makes
  the bar and progress variants possible at all.

  Usage, after mounting a Splide instance:

    window.SugarSliderChrome.attach(splide, hostEl, { dotMode: 'page' });

  hostEl is the element the chrome lives inside (normally the block root). The
  returned handle exposes detach(), which every host should call before it
  destroys its Splide instance.
*/
(function () {
  'use strict';

  if (window.SugarSliderChrome) return;

  // Every slider on a page mounts in the same task, and each one measures its
  // nav row and track edge right after writing its dots. A read that follows
  // another slider's write makes the browser lay the page out again, once per
  // slider. Measurements queue here instead and run together in one frame:
  // every read first, then every write.
  var measureQueue = [];
  var measureScheduled = false;

  function queueMeasure(inst) {
    if (measureQueue.indexOf(inst) === -1) measureQueue.push(inst);
    if (measureScheduled) return;
    measureScheduled = true;
    requestAnimationFrame(function () {
      measureScheduled = false;
      var batch = measureQueue.splice(0);
      var results = [];
      var i;
      for (i = 0; i < batch.length; i++) results.push(batch[i].measure());
      for (i = 0; i < batch.length; i++) batch[i].apply(results[i]);
    });
  }

  function dequeueMeasure(inst) {
    var at = measureQueue.indexOf(inst);
    if (at !== -1) measureQueue.splice(at, 1);
  }

  function attach(splide, host, opts) {
    if (window.__swOk === false) return null;
    if (!splide || !host) return null;
    var options = opts || {};
    var dotMode = options.dotMode === 'slide' ? 'slide' : 'page';

    // A host can draw a phone pagination beside the desktop one (data-sc-only): both live in
    // the same row and both are kept up to date; the stylesheet shows one per width.
    function twins(sel) {
      var first = host.querySelector(sel);
      if (!first) return [];
      var only = first.closest('[data-sc-only]');
      if (!only || !host.contains(only)) return [first];
      var out = [];
      Array.prototype.forEach.call(only.parentNode.children, function (el) {
        if (!el.hasAttribute('data-sc-only')) return;
        var match = el.matches(sel) ? el : el.querySelector(sel);
        if (match) out.push(match);
      });
      return out;
    }

    var pagEls = twins('[data-sc-pagination]');
    var fillEls = twins('[data-sc-progress-fill]');
    var fracEls = twins('[data-sc-fraction]');
    var prevBtns = host.querySelectorAll('[data-sc-arrow="prev"]');
    var nextBtns = host.querySelectorAll('[data-sc-arrow="next"]');
    var dotSets = [];
    var detached = false;

    function slideCount() {
      if (typeof splide.length === 'number') return splide.length;
      try {
        return splide.Components.Slides.getLength();
      } catch (e) {
        return 0;
      }
    }

    function isLoop() {
      try {
        return splide.is('loop');
      } catch (e) {
        return false;
      }
    }

    function lastIndex() {
      try {
        return splide.Components.Controller.getEnd();
      } catch (e) {
        return Math.max(0, slideCount() - 1);
      }
    }

    // Page mode groups slides into pages the way Splide's own pagination did:
    // 24 slides at 3 per page is 8 dots, not 24. Slide mode gives one dot per
    // slide. Splide's Controller owns the slide <-> page mapping, including
    // under loop, so ask it rather than dividing by perPage here.
    // Fixed- and auto-width cards in a track that stops on its last card rest once per card up to
    // that stop, wherever the page boundaries fall, so each resting position is a dot.
    function perCard() {
      var o = splide.options;
      return !!o.omitEnd && !!(o.fixedWidth || o.autoWidth) && !isLoop();
    }

    function toPage(index) {
      if (perCard()) return Math.min(index, lastIndex());
      if (dotMode === 'slide') return index;
      try {
        return splide.Components.Controller.toPage(index);
      } catch (e) {
        return index;
      }
    }

    function toIndex(page) {
      if (dotMode === 'slide' || perCard()) return page;
      try {
        return splide.Components.Controller.toIndex(page);
      } catch (e) {
        return page;
      }
    }

    function dotCount() {
      var n = slideCount();
      if (!n) return 0;
      if (perCard()) return lastIndex() + 1;
      if (dotMode === 'slide') return n;
      return toPage(n - 1) + 1;
    }

    function updateDots(index) {
      var page = toPage(index);
      dotSets.forEach(function (dots) {
        for (var i = 0; i < dots.length; i++) {
          var active = i === page;
          dots[i].classList.toggle('is-active', active);
          dots[i].setAttribute('aria-selected', active ? 'true' : 'false');
          dots[i].tabIndex = active ? 0 : -1;
        }
      });
    }

    // Dots already in the row are kept when there are as many as needed: a host that
    // knows its slide count draws them in its markup (see the snippet's `count`), so the
    // row has its height before this script arrives, and replacing them would change
    // nothing but the nodes.
    function hasDots(pagEl, n) {
      var kids = pagEl.children;
      if (kids.length !== n) return false;
      for (var i = 0; i < n; i++) {
        if (!kids[i].classList.contains('sc-dot')) return false;
      }
      return true;
    }

    function renderDots() {
      var n = dotCount();
      // A single resting position is not a choice; leave the row empty.
      if (n < 2) n = 0;
      dotSets = pagEls.map(function (pagEl) {
        var dots;
        if (hasDots(pagEl, n)) {
          dots = Array.prototype.slice.call(pagEl.children);
          for (var j = 0; j < n; j++) dots[j].classList.remove('sc-dot--hide-d', 'sc-dot--hide-m');
          return dots;
        }
        pagEl.innerHTML = '';
        dots = [];
        for (var i = 0; i < n; i++) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'sc-dot';
          b.setAttribute('role', 'tab');
          b.setAttribute('aria-label', (dotMode === 'slide' ? 'Go to slide ' : 'Go to page ') + (i + 1));
          b.dataset.scIndex = i;
          pagEl.appendChild(b);
          dots.push(b);
        }
        return dots;
      });
    }

    function updateProgress(index) {
      if (!fillEls.length) return;
      // Checkpoint model: each resting position fills one 1/N segment, so the
      // bar is full on the last one rather than short by a slide. Per slide counts
      // the positions the slider can rest on, not every slide: five cards three to
      // a view rest three ways, so the bar fills a third per move instead of jumping
      // from two fifths to full. Per page counts pages.
      var n;
      var step;
      if (dotMode === 'slide') {
        n = lastIndex() + 1;
        step = Math.min(index, n - 1);
      } else {
        n = dotCount() || 1;
        step = toPage(index);
      }
      var frac = (step + 1) / (n || 1);
      if (!isLoop() && index >= lastIndex()) frac = 1;
      var scale = 'scaleX(' + Math.max(0, Math.min(1, frac)) + ')';
      fillEls.forEach(function (fillEl) {
        fillEl.style.transform = scale;
      });
    }

    function updateFraction(index) {
      fracEls.forEach(function (fracEl) {
        var cur = fracEl.querySelector('[data-sc-fraction-current]');
        var tot = fracEl.querySelector('[data-sc-fraction-total]');
        if (cur) cur.textContent = index + 1;
        if (tot) tot.textContent = slideCount();
      });
    }

    function updateArrows(index) {
      var disablePrev = slideCount() < 2;
      var disableNext = disablePrev;
      if (!disablePrev && !isLoop()) {
        disablePrev = index <= 0;
        disableNext = index >= lastIndex();
      }
      var i;
      for (i = 0; i < prevBtns.length; i++) prevBtns[i].disabled = disablePrev;
      for (i = 0; i < nextBtns.length; i++) nextBtns[i].disabled = disableNext;
    }

    // When every slide already fits there is nowhere to navigate, so the chrome
    // hides itself. Ask the controller whether a second resting position exists
    // rather than Layout.isOverflow(), which has been seen to report false on a
    // track whose slides plainly overflow it.
    function isNavigable() {
      if (slideCount() < 2) return false;
      if (isLoop()) return true;
      // Fixed-width and auto-width cards get a resting position per card even when they all fit,
      // so there the row itself is measured: navigable only when the cards run past the track.
      if (splide.options.fixedWidth || splide.options.autoWidth) {
        var list = splide.Components.Elements.list;
        return !!list && list.scrollWidth > list.clientWidth + 1;
      }
      if (lastIndex() > 0) return true;
      // Free-drag tracks can scroll without discrete positions; there
      // Splide's own measurement is the only signal available.
      try {
        return !!splide.Components.Layout.isOverflow();
      } catch (e) {
        return true;
      }
    }

    function updateOverflow() {
      var navigable = isNavigable();
      var chrome = host.querySelectorAll('[data-sc-nav], [data-sc-arrow]');
      for (var i = 0; i < chrome.length; i++) chrome[i].hidden = !navigable;
    }

    // A host that knows its slides draws the row and the arrows as they will be at each
    // width, showing them or the dots at one width only (see the snippet's `slides`). From
    // here on the script decides at the width the page is at, so those width classes come off.
    function takeOverWidths() {
      var marked = host.querySelectorAll('.sc-nav--hide-d, .sc-nav--hide-m, .sc-arrow--hide-d, .sc-arrow--hide-m, .sc-pagination--hide-d, .sc-pagination--hide-m');
      for (var i = 0; i < marked.length; i++) {
        marked[i].classList.remove('sc-nav--hide-d', 'sc-nav--hide-m', 'sc-arrow--hide-d', 'sc-arrow--hide-m', 'sc-pagination--hide-d', 'sc-pagination--hide-m');
      }
    }

    function sync(index) {
      if (detached) return;
      var i = typeof index === 'number' ? index : splide.index;
      updateDots(i);
      updateProgress(i);
      updateFraction(i);
      updateArrows(i);
    }

    // An inside arrow is positioned against the slider root, which is as tall as the track
    // plus the nav row underneath it — so a plain 50% centres the arrow on the pair, sitting
    // visibly below the card. Publish what the nav row actually occupies (its box plus the
    // margin that separates it from the track) and let a host offset by half of it. Measured
    // rather than assumed, because the row's height changes with the pagination style, and
    // rebuild() already runs on mount and on every resize.
    function measureNavHeight() {
      var nav = host.querySelector('[data-sc-nav]');
      var h = 0;
      if (nav && !nav.hidden) {
        h = nav.offsetHeight + (parseFloat(getComputedStyle(nav).marginTop) || 0);
      }
      return h;
    }

    // overflow-clip-margin lets a card's shadow paint past the cut the track has to make,
    // but ink outside the box still counts towards the page's scrollable width: a track
    // sitting flush against the page edge pushes the whole page sideways by exactly the
    // room it asked for. Hand back only what fits before that edge. A full-bleed track
    // gets nothing, which costs it no shadow it could have shown anyway — its outer card
    // is already against the viewport. Measured against the page rather than the viewport
    // position, so the reading does not move with the sideways scroll it is there to stop.
    function measureClipRoom() {
      var max = parseFloat(getComputedStyle(host).getPropertyValue('--sc-clip-max'));
      if (!(max > 0)) return null;
      var track = host.querySelector('.splide__track');
      if (!track) return null;
      var right = track.getBoundingClientRect().right + window.scrollX;
      var edge = document.documentElement.clientWidth - right;
      return Math.max(0, Math.min(max, Math.floor(edge)));
    }

    // The two measured values, read together with every other slider's (see
    // queueMeasure) and written back in the same frame.
    var measured = {
      measure: function () {
        if (detached) return null;
        return { navH: measureNavHeight(), clipRoom: measureClipRoom() };
      },
      apply: function (r) {
        if (detached || !r) return;
        host.style.setProperty('--sc-nav-h', r.navH + 'px');
        if (r.clipRoom !== null) host.style.setProperty('--sc-clip-room', r.clipRoom + 'px');
      },
    };

    function rebuild() {
      renderDots();
      sync();
      updateOverflow();
      queueMeasure(measured);
    }

    function onPaginationClick(e) {
      var b = e.target.closest('.sc-dot');
      if (!b) return;
      splide.go(toIndex(parseInt(b.dataset.scIndex, 10)));
    }

    function onPrev() {
      splide.go('<');
    }

    function onNext() {
      splide.go('>');
    }

    pagEls.forEach(function (pagEl) {
      pagEl.addEventListener('click', onPaginationClick);
    });
    var i;
    for (i = 0; i < prevBtns.length; i++) prevBtns[i].addEventListener('click', onPrev);
    for (i = 0; i < nextBtns.length; i++) nextBtns[i].addEventListener('click', onNext);

    splide.on('move', sync);
    splide.on('updated refresh resized', rebuild);

    // attach() is called after mount(), so build straight away.
    rebuild();
    takeOverWidths();

    return {
      refresh: rebuild,
      detach: function () {
        if (detached) return;
        detached = true;
        dequeueMeasure(measured);
        pagEls.forEach(function (pagEl) {
          pagEl.removeEventListener('click', onPaginationClick);
          pagEl.innerHTML = '';
        });
        var j;
        for (j = 0; j < prevBtns.length; j++) {
          prevBtns[j].removeEventListener('click', onPrev);
          prevBtns[j].disabled = false;
        }
        for (j = 0; j < nextBtns.length; j++) {
          nextBtns[j].removeEventListener('click', onNext);
          nextBtns[j].disabled = false;
        }
        var chrome = host.querySelectorAll('[data-sc-nav], [data-sc-arrow]');
        for (j = 0; j < chrome.length; j++) chrome[j].hidden = false;
        host.style.removeProperty('--sc-nav-h');
        host.style.removeProperty('--sc-clip-room');
        try {
          splide.off('move', sync);
          splide.off('updated refresh resized', rebuild);
        } catch (e) {}
      },
    };
  }

  /*
    Linked sliders move as one, each inside its own frame: a picture's track and its quote's track
    are two sliders sharing a name, so the frames stay put and only what is in them slides. Any
    member leads. Its move (arrow, dot, swipe, autoplay) sends the others to the same slide, and
    while it is dragged they follow its pull live by the same share of a slide. A member with fewer
    slides stops at its last. Hosts call link(name, splide) after mount(); a destroyed member
    leaves its set, so a host that mounts again simply links again.
  */
  var linked = {};

  function slideStep(s, i) {
    var M = s.Components.Move;
    if (s.length < 2) return 0;
    return i + 1 < s.length ? M.toPosition(i + 1) - M.toPosition(i) : M.toPosition(i) - M.toPosition(i - 1);
  }

  function link(name, splide) {
    if (!name || !splide) return;
    var set = linked[name] || (linked[name] = { members: [], busy: false });
    if (set.members.indexOf(splide) !== -1) return;
    var others = function () {
      return set.members.filter(function (s) {
        return s !== splide;
      });
    };
    var lead = others()[0];
    set.members.push(splide);

    splide.on('move', function (index) {
      if (set.busy) return;
      set.busy = true;
      others().forEach(function (s) {
        try {
          s.Components.Controller.go(Math.min(index, s.length - 1), true);
        } catch (e) {}
      });
      set.busy = false;
    });
    // A follower still settling from the last move ignored that go(); line it up once this one lands.
    splide.on('moved', function (index) {
      if (set.busy) return;
      others().forEach(function (s) {
        var to = Math.min(index, s.length - 1);
        if (s.index !== to && !s.Components.Controller.isBusy()) s.Components.Controller.go(to, true);
      });
    });
    splide.on('scrolled', function () {
      others().forEach(function (s) {
        var to = Math.min(splide.index, s.length - 1);
        if (s.index !== to) s.Components.Controller.go(to, true);
      });
    });
    splide.on('dragging', function () {
      var step = slideStep(splide, splide.index);
      if (!step) return;
      var M = splide.Components.Move;
      var share = (M.getPosition() - M.toPosition(splide.index)) / step;
      others().forEach(function (s) {
        var Ms = s.Components.Move;
        Ms.translate(Ms.toPosition(s.index) + share * slideStep(s, s.index), true);
      });
    });
    splide.on('destroy', function () {
      var at = set.members.indexOf(splide);
      if (at !== -1) set.members.splice(at, 1);
    });

    if (lead && lead.index !== splide.index) splide.Components.Controller.go(Math.min(lead.index, splide.length - 1), true);
  }

  window.SugarSliderChrome = { attach: attach, link: link };
})();
